/* global axios */
import ApiClient from './ApiClient';

const formatColorForGmail = (hexColor) => {
  if (!hexColor) return null;

  // Calculate contrasting text color (black or white)
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const textColor = luminance > 0.5 ? '#000000' : '#ffffff';

  return {
    backgroundColor: hexColor,
    textColor,
  };
};

class FoldersAPI extends ApiClient {
  constructor() {
    super('inboxes', { accountScoped: true });
  }

  /**
   * Get the URL for folders endpoints
   * @param {number} inboxId - The inbox ID
   * @returns {string} The folders URL
   */
  getFoldersUrl(inboxId) {
    return `${this.url}/${inboxId}/folders`;
  }

  /**
   * List all folders for an inbox
   * @param {number} inboxId - The inbox ID
   * @returns {Promise} Axios promise
   */
  list(inboxId) {
    return axios.get(this.getFoldersUrl(inboxId));
  }

  /**
   * Create a new folder
   * @param {number} inboxId - The inbox ID
   * @param {Object} params - Folder parameters
   * @param {string} params.name - Folder name (required)
   * @param {string} [params.parentSourceId] - Parent folder source_id (optional, for nested folders)
   * @param {string} [params.color] - Folder color in hex format (optional, Gmail only)
   * @returns {Promise} Axios promise
   */
  create(inboxId, { name, parentSourceId, color }) {
    const payload = { name };

    if (parentSourceId) {
      payload.parent_source_id = parentSourceId;
    }

    // Format color for Gmail API (requires backgroundColor + textColor object)
    const formattedColor = formatColorForGmail(color);
    if (formattedColor) {
      payload.color = formattedColor;
    }

    return axios.post(this.getFoldersUrl(inboxId), payload);
  }

  /**
   * Update an existing folder
   * @param {number} inboxId - The inbox ID
   * @param {string} sourceId - The folder's source_id (URL-encoded automatically)
   * @param {Object} params - Update parameters
   * @param {string} [params.name] - New folder name
   * @param {string} [params.color] - New folder color (Gmail only)
   * @returns {Promise} Axios promise
   */
  update(inboxId, sourceId, { name, color }) {
    const payload = {};

    if (name !== undefined) {
      payload.name = name;
    }

    // Format color for Gmail API (requires backgroundColor + textColor object)
    if (color !== undefined) {
      const formattedColor = formatColorForGmail(color);
      if (formattedColor) {
        payload.color = formattedColor;
      }
    }

    const encodedSourceId = encodeURIComponent(sourceId);
    return axios.patch(
      `${this.getFoldersUrl(inboxId)}/${encodedSourceId}`,
      payload
    );
  }

  /**
   * Delete a folder
   * @param {number} inboxId - The inbox ID
   * @param {string} sourceId - The folder's source_id (URL-encoded automatically)
   * @returns {Promise} Axios promise
   */
  delete(inboxId, sourceId) {
    const encodedSourceId = encodeURIComponent(sourceId);
    return axios.delete(`${this.getFoldersUrl(inboxId)}/${encodedSourceId}`);
  }

  /**
   * Force sync folders from email provider
   * @param {number} inboxId - The inbox ID
   * @returns {Promise} Axios promise
   */
  sync(inboxId) {
    return axios.post(`${this.getFoldersUrl(inboxId)}/sync`);
  }
}

export default new FoldersAPI();
